import { useCallback, useEffect, useRef, useState } from "react";
import {
    useCurrentMedia,
    usePromptLabels,
    usePredictionsForFile,
    useAnnotationsForFile
} from "@rapid/store";
import { MediaItemStatus } from "@rapid/types/media";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { Flows, BuildSteps } from "@rapid/types/flows";
import { useAutoPredictionsTrigger } from "@rapid/contexts/AutoPredictionsContext";
import { useLocation } from "react-router-dom";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useIsGeneratingPredictions } from "@rapid/hooks/useIsGeneratingPredictions";

const SCRUB_DEBOUNCE_MS = 100;
const MAX_AUTO_PREDICTION_RETRIES = 3;

/**
 * AutoPredictions - Automatically triggers the "Find Objects" button when a frame/image
 * is first visited in the BUILD flow. This component should be mounted inside the
 * AggregatedFlowContextProvider to have access to FlowContext.
 */
export const AutoPredictions = () => {
    const { flow, step } = useFlowState();
    const location = useLocation();
    const currentMedia = useCurrentMedia();
    const promptLabels = usePromptLabels();
    const predictionsForCurrentFile = usePredictionsForFile(currentMedia?.file.id);
    const annotationsForCurrentFile = useAnnotationsForFile(currentMedia?.file.id);
    const { triggerPredictions, setIsAutoPredictionPending } = useAutoPredictionsTrigger();
    const { setFlowLocked } = useFlow();
    const isGeneratingPredictions = useIsGeneratingPredictions();

    // Track frames/images that have had auto-predictions generated
    const [autoGeneratedFrameIds, setAutoGeneratedFrameIds] = useState<Set<string>>(new Set());
    const isAutoGeneratingRef = useRef(false);
    const pendingTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});
    const triggeredThisRunRef = useRef(false);
    const autoPredictionLockOwnerRef = useRef<string | null>(null);
    const retryCountRef = useRef<Record<string, number>>({});

    const unlockIfOwnedBy = useCallback(
        (mediaItemId: string, onUnlock?: () => void) => {
            if (autoPredictionLockOwnerRef.current === mediaItemId) {
                setFlowLocked(false);
                autoPredictionLockOwnerRef.current = null;
                onUnlock?.();
            }
        },
        [setFlowLocked]
    );

    const unlockUnconditionally = useCallback(() => {
        if (autoPredictionLockOwnerRef.current != null) {
            setFlowLocked(false);
            autoPredictionLockOwnerRef.current = null;
        }
    }, [setFlowLocked]);

    // Track promptLabels size in a ref to avoid triggering effect on label changes
    // We only care if there are ANY labels, not when specific labels change
    const hasObjectCardsRef = useRef(promptLabels.size > 0);
    hasObjectCardsRef.current = promptLabels.size > 0;

    // Auto-generate predictions when frame/image is first visited in BUILD flow
    useEffect(() => {
        triggeredThisRunRef.current = false;

        const mediaItemForInference = currentMedia?.file?.isVideo
            ? currentMedia?.frame
            : currentMedia?.file;

        const capturedMediaItemId = mediaItemForInference?.id;
        const isInPromptStep = location.pathname.includes("/build/prompt");

        // Only trigger in PROMPT step of BUILD flow
        // Use pathname check as it's more reliable than the step state
        if (flow !== Flows.BUILD || !isInPromptStep) {
            unlockUnconditionally();
            setIsAutoPredictionPending(false);
            return;
        }

        // Need prompts or object cards to generate predictions
        // Use ref to avoid re-triggering when labels change (e.g., user renames a box)
        if (!hasObjectCardsRef.current) {
            if (capturedMediaItemId)
                unlockIfOwnedBy(capturedMediaItemId, () => setIsAutoPredictionPending(false));
            return;
        }

        // Need media item for inference
        if (!mediaItemForInference) {
            if (capturedMediaItemId)
                unlockIfOwnedBy(capturedMediaItemId, () => setIsAutoPredictionPending(false));
            return;
        }

        // Check if we already auto-generated for this media item
        if (autoGeneratedFrameIds.has(mediaItemForInference.id)) {
            if (!isGeneratingPredictions)
                unlockIfOwnedBy(capturedMediaItemId!, () => setIsAutoPredictionPending(false));
            return;
        }

        // Check if there are already predictions or annotations for this frame/image
        // We check BOTH because:
        // - predictions indicate inference has already run
        // - annotations indicate user has already worked on this frame (drawn boxes, confirmed labels, etc.)
        const currentFrameSegment = currentMedia?.frameSegment ?? 0;
        const frameKey = currentMedia?.file?.isVideo
            ? `${currentMedia?.file.id}_${currentFrameSegment}`
            : mediaItemForInference.id;
        const currentFramePredictions = predictionsForCurrentFile?.[frameKey] || [];
        if (currentFramePredictions.length > 0) {
            unlockIfOwnedBy(capturedMediaItemId!, () => setIsAutoPredictionPending(false));
            return;
        }

        // Also check for existing annotations - if user has any annotations on this frame,
        // don't auto-trigger predictions (they may have manually drawn boxes or changed labels)
        const currentFrameAnnotations = annotationsForCurrentFile?.[mediaItemForInference.id] || [];
        if (currentFrameAnnotations.length > 0) {
            unlockIfOwnedBy(capturedMediaItemId!, () => setIsAutoPredictionPending(false));
            return;
        }

        // Wait for upload to complete - lock flow during this wait for unified loading state
        if (mediaItemForInference.status !== MediaItemStatus.COMPLETED) {
            autoPredictionLockOwnerRef.current = capturedMediaItemId ?? null;
            setFlowLocked(true);
            setIsAutoPredictionPending(true);
            return;
        }

        // Check if upload result is available (needed for inference)
        const upload =
            mediaItemForInference.uploadResizedResult || mediaItemForInference.uploadResult;
        if (!upload?.filename) {
            unlockIfOwnedBy(capturedMediaItemId!, () => setIsAutoPredictionPending(false));
            return;
        }

        // For videos, ensure we have all required metadata before triggering
        if (currentMedia?.file?.isVideo) {
            // Check that the parent file has natural dimensions (video metadata loaded)
            if (!currentMedia.file.metadata?.naturalDimensions) {
                unlockIfOwnedBy(capturedMediaItemId!, () => setIsAutoPredictionPending(false));
                return;
            }

            // Check that the frame has a segment number (required for inference)
            if (typeof mediaItemForInference.metadata?.segment !== "number") {
                unlockIfOwnedBy(capturedMediaItemId!, () => setIsAutoPredictionPending(false));
                return;
            }
        }

        // Cancel any existing timeout for this media item
        if (pendingTimeoutRef.current[capturedMediaItemId!]) {
            clearTimeout(pendingTimeoutRef.current[capturedMediaItemId!]);
        }

        autoPredictionLockOwnerRef.current = capturedMediaItemId ?? null;
        setFlowLocked(true);
        setIsAutoPredictionPending(true);

        const timeoutId = setTimeout(() => {
            delete pendingTimeoutRef.current[capturedMediaItemId!];

            triggeredThisRunRef.current = true;

            // Mark this media item as being processed NOW (right before triggering)
            setAutoGeneratedFrameIds((prev) => new Set(prev).add(capturedMediaItemId!));

            isAutoGeneratingRef.current = true;

            triggerPredictions()
                .catch((error) => {
                    console.error("[AutoPredictions] Auto-prediction failed:", error);
                    const retries = (retryCountRef.current[capturedMediaItemId!] ?? 0) + 1;
                    retryCountRef.current[capturedMediaItemId!] = retries;
                    if (retries < MAX_AUTO_PREDICTION_RETRIES) {
                        setAutoGeneratedFrameIds((prev) => {
                            const newSet = new Set(prev);
                            newSet.delete(capturedMediaItemId!);
                            return newSet;
                        });
                    } else {
                        console.warn(
                            "[AutoPredictions] Max retries reached for",
                            capturedMediaItemId
                        );
                        unlockIfOwnedBy(capturedMediaItemId!, () =>
                            setIsAutoPredictionPending(false)
                        );
                    }
                })
                .finally(() => {
                    isAutoGeneratingRef.current = false;
                    unlockIfOwnedBy(capturedMediaItemId!, () => setIsAutoPredictionPending(false));
                });
        }, SCRUB_DEBOUNCE_MS);

        // Store timeout ref
        pendingTimeoutRef.current[capturedMediaItemId!] = timeoutId;

        // Cleanup timeout on unmount or when dependencies change
        return () => {
            clearTimeout(timeoutId);
            delete pendingTimeoutRef.current[capturedMediaItemId!];
            if (!triggeredThisRunRef.current)
                unlockIfOwnedBy(capturedMediaItemId!, () => setIsAutoPredictionPending(false));
        };
    }, [
        flow,
        location.pathname,
        currentMedia?.frame?.id,
        currentMedia?.frame?.status,
        currentMedia?.frame?.uploadResult,
        currentMedia?.frame?.uploadResizedResult,
        currentMedia?.frame?.metadata?.segment,
        currentMedia?.file?.id,
        currentMedia?.file?.status,
        currentMedia?.file?.uploadResult,
        currentMedia?.file?.uploadResizedResult,
        currentMedia?.file?.isVideo,
        currentMedia?.file?.metadata?.naturalDimensions,
        currentMedia?.frameSegment,
        autoGeneratedFrameIds,
        predictionsForCurrentFile,
        annotationsForCurrentFile,
        triggerPredictions,
        unlockIfOwnedBy,
        unlockUnconditionally,
        setIsAutoPredictionPending,
        isGeneratingPredictions
    ]);

    // This component doesn't render anything
    return null;
};
